import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/Icon';
import { api } from '@/lib/api';
import { usePlan } from '@/hooks/usePlan';

export const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { plan } = usePlan();
  const planLabel = t(`billing.${plan}`) || plan.charAt(0).toUpperCase() + plan.slice(1);

  const planTheme = {
    trial: { icon: '#f59e0b', border: '#f59e0b', bg: 'rgba(245, 158, 11, 0.15)' },
    starter: { icon: '#006cd4', border: '#006cd4', bg: 'rgba(0, 108, 212, 0.15)' },
    growth: { icon: '#00d4aa', border: '#00d4aa', bg: 'rgba(0, 212, 170, 0.15)' },
    pro: { icon: '#8b5cf6', border: '#8b5cf6', bg: 'rgba(139, 92, 246, 0.15)' },
    enterprise: { icon: '#f97316', border: '#f97316', bg: 'rgba(249, 115, 22, 0.15)' },
  }[plan] || { icon: '#006cd4', border: '#006cd4', bg: 'rgba(0, 108, 212, 0.15)' };

  const { data: analytics, isLoading } = useQuery({
    queryKey: ['analytics-dashboard'],
    queryFn: async () => {
      const response = await api.get('/analytics/dashboard');
      return response.data.data;
    },
  });

  const { data: conversationsData } = useQuery({
    queryKey: ['recent-conversations'],
    queryFn: async () => {
      const response = await api.get('/conversations?limit=5');
      return response.data.data || [];
    },
  });

  const { data: leadsData } = useQuery({
    queryKey: ['recent-leads'],
    queryFn: async () => {
      const response = await api.get('/leads?limit=5');
      return response.data.data || [];
    },
  });

  const stats = [
    { label: t('dashboard.totalConversations'), value: analytics?.totalConversations || 0, icon: 'message' },
    { label: t('dashboard.totalLeads'), value: analytics?.totalLeads || 0, icon: 'bullseye' },
    { label: t('dashboard.conversionRate'), value: `${analytics?.conversionRate || 0}%`, icon: 'chart-line' },
    { label: t('dashboard.todaysConversations'), value: analytics?.todayConversations || 0, icon: 'chart-column' },
  ];

  const recentConversations = conversationsData || [];
  const recentLeads = leadsData || [];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-500/20 text-green-400';
      case 'new': return 'bg-green-500/20 text-green-400';
      case 'converted': return 'bg-blue-500/20 text-blue-400';
      case 'contacted': return 'bg-blue-500/20 text-blue-400';
      case 'closed': return 'bg-gray-500/20 text-gray-400';
      default: return 'bg-gray-500/20 text-gray-400';
    }
  };

  return (
    <div>
      <h1 className="text-3xl font-bold mb-8">{t('dashboard.title')}</h1>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {stats.map((stat) => (
          <div
            key={stat.label}
            className="bg-[#12121a] border border-white/10 rounded-xl p-6"
          >
            <div className="text-3xl mb-2 text-[#006cd4]"><Icon name={stat.icon} /></div>
            <div className="text-2xl font-bold">{isLoading ? '...' : stat.value}</div>
            <div className="text-sm text-gray-400">{stat.label}</div>
          </div>
        ))}
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Recent Conversations */}
        <div className="bg-[#12121a] border border-white/10 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">{t('dashboard.recentConversations')}</h2>
            <button 
              onClick={() => navigate('/conversations')}
              className="text-sm text-[#006cd4] hover:underline"
            >
              {t('dashboard.viewAll')}
            </button>
          </div>
          {recentConversations.length === 0 ? (
            <div className="text-gray-400 text-center py-8">{t('dashboard.noConversations')}</div>
          ) : (
            <div className="space-y-3">
              {recentConversations.map((conv: any) => (
                <div 
                  key={conv.id} 
                  className="flex items-center justify-between p-3 bg-[#1a1a25] rounded-lg hover:bg-[#222230] cursor-pointer transition-colors"
                  onClick={() => navigate(`/conversations/${conv.id}`)}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-[#006cd4]/20 flex items-center justify-center text-[#006cd4] text-sm">
                      {conv.visitor_name?.charAt(0) || conv.visitor_email?.charAt(0) || '?'}
                    </div>
                    <div>
                      <div className="font-medium text-sm">{conv.visitor_name || t('common.anonymous')}</div>
                      <div className="text-xs text-gray-500">{conv.visitor_email || t('common.noEmail')}</div>
                    </div>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs capitalize ${getStatusColor(conv.status)}`}>
                    {conv.status}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent Leads */}
        <div className="bg-[#12121a] border border-white/10 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">{t('dashboard.recentLeads')}</h2>
            <button 
              onClick={() => navigate('/leads')}
              className="text-sm text-[#006cd4] hover:underline"
            >
              {t('dashboard.viewAll')}
            </button>
          </div>
          {recentLeads.length === 0 ? (
            <div className="text-gray-400 text-center py-8">{t('dashboard.noLeads')}</div>
          ) : (
            <div className="space-y-3">
              {recentLeads.map((lead: any) => (
                <div 
                  key={lead.id} 
                  className="flex items-center justify-between p-3 bg-[#1a1a25] rounded-lg hover:bg-[#222230] cursor-pointer transition-colors"
                  onClick={() => navigate(`/leads/${lead.id}`)}
                >
                  <div>
                    <div className="font-medium text-sm">{lead.name}</div>
                    <div className="text-xs text-gray-500">{lead.email}</div>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs capitalize ${getStatusColor(lead.status)}`}>
                    {lead.status.replace('_', ' ')}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-[#12121a] border border-white/10 rounded-xl p-6">
        <h2 className="text-xl font-semibold mb-4">{t('dashboard.quickActions')}</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            onClick={() => navigate('/billing')}
            className="flex items-center gap-3 p-4 bg-[#1a1a25] rounded-lg hover:bg-[#222230] transition-colors text-left border-l-4"
            style={{ borderLeftColor: planTheme.border }}
          >
            <span
              className="w-10 h-10 rounded-lg flex items-center justify-center text-xl"
              style={{ backgroundColor: planTheme.bg, color: planTheme.icon }}
            >
              <Icon name="credit-card" />
            </span>
            <div>
              <div className="font-medium">
                <span>{t('dashboard.currentPlan')} </span>
                <span style={{ color: planTheme.icon }}>{planLabel}</span>
              </div>
              <div className="text-sm text-gray-400">{t('dashboard.currentPlanDesc')}</div>
            </div>
          </button>
          <button
            onClick={() => navigate('/widget')}
            className="flex items-center gap-3 p-4 bg-[#1a1a25] rounded-lg hover:bg-[#222230] transition-colors text-left"
          >
            <span className="text-2xl text-[#006cd4]"><Icon name="bolt" /></span>
            <div>
              <div className="font-medium">{t('dashboard.configureWidget')}</div>
              <div className="text-sm text-gray-400">{t('dashboard.configureWidgetDesc')}</div>
            </div>
          </button>
          <button
            onClick={() => navigate('/proposals')}
            className="flex items-center gap-3 p-4 bg-[#1a1a25] rounded-lg hover:bg-[#222230] transition-colors text-left"
          >
            <span className="text-2xl text-[#006cd4]"><Icon name="file-lines" /></span>
            <div>
              <div className="font-medium">{t('dashboard.newProposal')}</div>
              <div className="text-sm text-gray-400">{t('dashboard.newProposalDesc')}</div>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
};
